import type { RvExpandedDetails } from '~/types/rv'

const DEBOUNCE_MS = 5000

/**
 * Composable that queues and sends RV listing viewable-impression tracking
 * (ListingSetViewed / Product List Viewed). Call queueViewableImpression when
 * an RV card becomes viewable; impressions are debounced and sent after 5s or
 * on unmount.
 *
 * pageNumber and pageSize are read at track time (not at init), so pass refs
 * or getters when they change (e.g. pagination) to keep analytics correct.
 */
export default function useRvViewableImpressions({ pageNumber, pageSize }: {
  pageNumber: MaybeRefOrGetter<number>
  pageSize: MaybeRefOrGetter<number>
}) {
  const { pageSource } = usePageSource()
  const absoluteLocalePath = useAbsoluteLocalePath()
  const searchData = useSearchData()
  const { checkIfRvIsFavourited } = useFavouritesData()
  const { getDynamicImage } = useImage()

  const visibilityQueue = ref<RvExpandedDetails[]>([])
  const queueTimer = ref<ReturnType<typeof setTimeout>>()

  function trackViewableImpressions(cta?: string, _requestId?: string) {
    const queueToSend = [...visibilityQueue.value]
    visibilityQueue.value = []

    if (queueToSend.length === 0) return

    const currentPageNumber = toValue(pageNumber)
    const currentPageSize = toValue(pageSize)

    const entries = queueToSend.flatMap((rv, index) => {
      const details = getRvDetails(rv)
      const { rvAliasName, rvCountry, rvDefaultPrice, rvDiscountedAverageNightlyPrice, rvId, rvLatitude, rvLongitude, rvPhotos } = details
      const image = getDynamicImage({
        path: rvPhotos?.[0]?.Path,
      })

      if (!image) {
        return []
      }

      const listing = toTrackedListing(details, {
        distanceKm: distanceAwayWithoutUnits(rvLatitude, rvLongitude, searchData.value.location?.coordinates?.lat, searchData.value.location?.coordinates?.lng),
        distanceUnit: countryCodeToDistanceUnitShort(rvCountry),
        heroImage: image,
        isFavourite: Boolean(checkIfRvIsFavourited(rvId)),
        listPosition: index + 1,
        nightlyRate: Math.max(rvDiscountedAverageNightlyPrice, 0) || rvDefaultPrice,
      })

      if (!listing) {
        return []
      }

      return [{
        listing,
        rvUrl: absoluteLocalePath(generateRvPath(rvAliasName ?? '')),
      }]
    })

    if (entries.length === 0) return

    trackListingSetViewed({
      params: {
        cta,
        listingPageNumber: currentPageNumber,
        listingPageSize: currentPageSize,
        pageSource: pageSource.value,
        listings: entries.map(({ listing }) => listing),
      },
      additionalParameters: {
        rvUrlListings: entries.map(({ rvUrl }) => ({ rvUrl })),
      },
    })
  }

  function queueViewableImpression({ rv, cta, requestId }: {
    rv: RvExpandedDetails
    cta?: string
    requestId?: string
  }) {
    visibilityQueue.value.push(rv)
    clearTimeout(queueTimer.value)
    queueTimer.value = setTimeout(() => {
      trackViewableImpressions(cta, requestId)
    }, DEBOUNCE_MS)
  }

  onBeforeUnmount(() => {
    clearTimeout(queueTimer.value)
    trackViewableImpressions()
  })

  return {
    queueViewableImpression,
  }
}
